<template>
  <div class="gifts-inbox-page">
    <div class="container">
      <router-link to="/my-people" class="back-link">&larr; My People</router-link>
      <h1>Gift Ideas</h1>

      <Spinner :show="loading" message="Loading your gift ideas..." />

      <!-- Empty state -->
      <div v-if="!loading && proposals.length === 0" class="empty-state">
        <div class="empty-mark">🎁</div>
        <h2>No gift ideas yet</h2>
        <p>
          Add the people you care about with their birthdays and special dates.
          When an occasion is coming up, we'll find gift ideas and drop them here
          for you to approve.
        </p>
        <router-link to="/my-people" class="button primary">Go to My People</router-link>
      </div>

      <template v-if="!loading && proposals.length > 0">
        <!-- Awaiting your call -->
        <section v-if="pending.length > 0" class="inbox-section">
          <h2 class="section-title">Waiting for you</h2>
          <router-link
            v-for="p in pending"
            :key="p.id"
            :to="`/gifts/${p.id}`"
            class="proposal-row pending"
          >
            <div class="thumb" :class="{ placeholder: !firstImage(p) }">
              <img v-if="firstImage(p)" :src="firstImage(p)" :alt="p.recipientName" />
              <span v-else class="placeholder-mark">{{ p.kind === 'gesture' ? '☏' : '✦' }}</span>
            </div>
            <div class="row-body">
              <div class="row-title">{{ p.kind === 'gesture' ? 'Something to do for' : 'Gift ideas for' }} {{ p.recipientName || 'someone' }}</div>
              <div class="row-sub">
                <span v-if="p.occasion" class="occasion">{{ p.occasion }}</span>
                <span v-if="p.kind === 'gesture'" class="idea-count">{{ p.proposals && p.proposals[0] ? p.proposals[0].title : '' }}</span>
                <span v-else class="idea-count">{{ ideaCount(p) }} idea{{ ideaCount(p) === 1 ? '' : 's' }}</span>
              </div>
            </div>
            <span class="row-cta">Review &rarr;</span>
          </router-link>
        </section>

        <!-- Already decided -->
        <section v-if="decided.length > 0" class="inbox-section">
          <h2 class="section-title">Decided</h2>
          <router-link
            v-for="p in decided"
            :key="p.id"
            :to="`/gifts/${p.id}`"
            class="proposal-row decided"
          >
            <div class="status-dot" :class="statusMeta(p.status).cls">{{ statusMeta(p.status).icon }}</div>
            <div class="row-body">
              <div class="row-title">{{ p.recipientName || 'someone' }}</div>
              <div class="row-sub">
                <span v-if="p.occasion" class="occasion">{{ p.occasion }}</span>
                <span class="status-label">{{ statusMeta(p.status).label }}</span>
              </div>
            </div>
            <span class="row-cta subtle">View</span>
          </router-link>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/SpinnerComp.vue";
import { db, auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";

const STATUS_META = {
  approved: { label: "Approved", icon: "✓", cls: "ok" },
  purchasing: { label: "Making it now", icon: "✦", cls: "pending-purchase" },
  purchased: { label: "On its way", icon: "🎁", cls: "ok" },
  purchase_failed: { label: "Couldn't complete", icon: "⚠️", cls: "failed" },
  declined: { label: "Passed", icon: "—", cls: "muted" },
  gesture_chosen: { label: "You're doing it yourself", icon: "♡", cls: "ok" },
  pooled: { label: "Giving it together", icon: "♡", cls: "ok" },
};

export default {
  name: "GiftsInboxPage",
  components: { Spinner },
  data() {
    return {
      proposals: [],
      loading: true,
      authUnsub: null,
      proposalsUnsub: null,
    };
  },
  computed: {
    // Newest first; guards against a missing createdAt.
    sorted() {
      return [...this.proposals].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    },
    pending() {
      return this.sorted.filter((p) => p.status === "pending_approval");
    },
    decided() {
      return this.sorted.filter((p) => p.status !== "pending_approval");
    },
  },
  created() {
    // Wait for auth to settle (hard refresh rehydrates async), then subscribe.
    this.authUnsub = onAuthStateChanged(auth, (user) => {
      this.teardownProposals();
      if (!user) {
        this.proposals = [];
        this.loading = false;
        return;
      }
      this.subscribe(user.uid);
    });
  },
  beforeUnmount() {
    this.teardownProposals();
    if (this.authUnsub) this.authUnsub();
  },
  methods: {
    subscribe(uid) {
      this.loading = true;
      // where(userId ==) only — no orderBy, so no composite index is needed;
      // sorting happens client-side in `sorted`.
      const q = query(collection(db, "giftProposals"), where("userId", "==", uid));
      this.proposalsUnsub = onSnapshot(
        q,
        (snap) => {
          this.proposals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.$toast.error("Couldn't load your gift ideas.");
        },
      );
    },
    teardownProposals() {
      if (this.proposalsUnsub) {
        this.proposalsUnsub();
        this.proposalsUnsub = null;
      }
    },
    ideaCount(p) {
      return Array.isArray(p.proposals) ? p.proposals.length : 0;
    },
    firstImage(p) {
      const first = Array.isArray(p.proposals) ? p.proposals.find((x) => x.imageUrl) : null;
      return first ? first.imageUrl : null;
    },
    statusMeta(status) {
      return STATUS_META[status] || { label: "Done", icon: "✓", cls: "ok" };
    },
  },
};
</script>

<style scoped>
.gifts-inbox-page { padding: 24px 16px 64px; }
.container { max-width: 720px; margin: 0 auto; }

.back-link {
  display: inline-block;
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 8px;
}
.back-link:hover { color: var(--violet-lush); }

.gifts-inbox-page h1 { color: var(--rich-black); margin: 0 0 24px; }

.empty-state { text-align: center; padding: 56px 16px; }
.empty-mark { font-size: 2.6rem; }
.empty-state h2 { color: var(--rich-black); margin: 12px 0 8px; }
.empty-state p { color: var(--violet-lush, #666); max-width: 420px; margin: 0 auto 24px; line-height: 1.5; }

.inbox-section { margin-bottom: 28px; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--violet-lush, #888); margin: 0 0 12px; }

.proposal-row {
  display: flex; align-items: center; gap: 14px;
  border: 2px solid #e6e6e6; border-radius: 14px; background: #fff;
  padding: 12px; margin-bottom: 12px; text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.proposal-row:hover { border-color: var(--hollywood-cerise); box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
.proposal-row.pending { border-color: #ffd0de; }
.proposal-row.pending:hover { border-color: var(--hollywood-cerise); }

.thumb {
  flex: 0 0 56px; width: 56px; height: 56px; border-radius: 10px; overflow: hidden;
  background: #f4f4f4; display: flex; align-items: center; justify-content: center;
}
.thumb img { width: 100%; height: 100%; object-fit: cover; }
.thumb.placeholder { background: linear-gradient(135deg, #ffe6ed, #ede7ff); }
.placeholder-mark { font-size: 1.4rem; color: var(--hollywood-cerise); }

.status-dot {
  flex: 0 0 40px; width: 40px; height: 40px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
  background: #f1f1f1;
}
.status-dot.ok { background: #e6f7ec; }
.status-dot.failed { background: #fdeaea; }
.status-dot.muted { background: #f1f1f1; color: #999; }
.status-dot.pending-purchase { background: #fff2df; }

.row-body { flex: 1; min-width: 0; }
.row-title { font-weight: 600; color: var(--rich-black); }
.row-sub { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 3px; font-size: 0.85rem; color: #666; }
.row-sub .occasion { color: var(--hollywood-cerise); font-weight: 600; }
.status-label { color: #777; }

.row-cta { color: var(--hollywood-cerise); font-weight: 700; white-space: nowrap; font-size: 0.9rem; }
.row-cta.subtle { color: #999; font-weight: 500; }
</style>
